import { CORRELATION_ID_HEADER } from "@shared/lib/analytics"
import { analyticsService } from "@sustainability/analytics-frontend"
import { AxiosError } from "axios"

import { navigateToPath } from "./navigate"

const SERVER_ERROR_CODE = 500

/**
 * Extracts the `traceparent` header from the outgoing request.
 * XMLHttpRequest stores custom headers in `_ajaxData.xh` internally.
 */
const getTraceparentFromRequest = (error: AxiosError): string | undefined => {
	const request = error.request as XMLHttpRequest & {
		_ajaxData?: { xh?: { n: string; v: string }[] }
	}
	return request?._ajaxData?.xh?.find(h => h.n === "traceparent")?.v
}

const track = (statusCode: number, error: AxiosError) => {
	const WARNING_SEVERITY_LEVEL = 2

	const appInsightsCustomProps = {
		product: import.meta.env.VITE_APP_NAME,
		appVersion: import.meta.env.VITE_APP_VERSION,
		correlationId:
			error.response?.headers[CORRELATION_ID_HEADER.toLowerCase()],
		timestamp: new Date().toISOString(),
	}

	if (statusCode >= SERVER_ERROR_CODE) {
		analyticsService.reactPlugin.trackException(
			{
				exception: error,
			},
			appInsightsCustomProps,
		)
		return
	}

	analyticsService.reactPlugin.trackTrace(
		{
			message: error.message,
			severityLevel: WARNING_SEVERITY_LEVEL,
		},
		appInsightsCustomProps,
	)
}

export const statusCheckInterceptor = async (
	error: AxiosError,
): Promise<AxiosError> => {
	const errorCodeToPath: Record<number, string> = {
		401: "/unauthorized",
		403: "/access-denied",
		404: "/not-found",
		500: "/service-unavailable",
		503: "/service-unavailable",
	}
	const statusCode = error.response?.status || 0
	let errorPagePath = errorCodeToPath[statusCode]

	// Special handling for 404 on transfer pages
	if (
		statusCode === 404 &&
		globalThis.location.pathname.includes("/transfer")
	) {
		errorPagePath = "/no-transaction-found"
	}

	track(statusCode, error)

	if (errorPagePath) {
		const traceparent = getTraceparentFromRequest(error)

		const params = new URLSearchParams()
		if (statusCode >= SERVER_ERROR_CODE) {
			params.set("backUrl", globalThis.location.pathname)
			params.set("errorMessage", error.message)
		}
		if (traceparent) {
			params.set("traceparent", traceparent)
		}
		const queryString = params.toString()

		const fullPath = queryString
			? `${errorPagePath}?${queryString}`
			: errorPagePath

		navigateToPath(fullPath)
	}

	throw error
}
